import { Link } from 'react-router-dom';
import { Clock, Video } from 'lucide-react';
import { LessonStatus, type LessonSummary } from '@tutor/shared';
import { Avatar, Badge, Button, cn } from '@tutor/ui';
import { formatDuration, formatRelativeDay, formatTime } from '../lib/format';

const STATUS_TONE = {
  [LessonStatus.SCHEDULED]: 'neutral',
  [LessonStatus.IN_PROGRESS]: 'success',
  [LessonStatus.COMPLETED]: 'info',
  [LessonStatus.CANCELLED]: 'danger',
  [LessonStatus.NO_SHOW]: 'warning',
} as const;

const STATUS_LABEL = {
  [LessonStatus.SCHEDULED]: 'Scheduled',
  [LessonStatus.IN_PROGRESS]: 'Live now',
  [LessonStatus.COMPLETED]: 'Completed',
  [LessonStatus.CANCELLED]: 'Cancelled',
  [LessonStatus.NO_SHOW]: 'No show',
} as const;

export interface LessonCardProps {
  lesson: LessonSummary;
  /** Tutors see the student; students see the tutor. */
  perspective: 'tutor' | 'student';
  showDay?: boolean;
  className?: string;
}

export function LessonCard({ lesson, perspective, showDay = false, className }: LessonCardProps) {
  const counterpart =
    perspective === 'tutor'
      ? (lesson.students[0] ?? null)
      : { ...lesson.tutor };

  const extraStudents = perspective === 'tutor' ? Math.max(0, lesson.students.length - 1) : 0;
  const basePath = perspective === 'tutor' ? '/tutor' : '/student';
  const isLive = lesson.status === LessonStatus.IN_PROGRESS;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300',
        isLive && 'border-emerald-300 bg-emerald-50/40',
        className,
      )}
    >
      <div className="flex w-16 shrink-0 flex-col">
        <span className="text-lg font-semibold tabular-nums text-slate-900">
          {formatTime(lesson.scheduledStart)}
        </span>
        <span className="text-xs text-slate-500">
          {showDay ? formatRelativeDay(lesson.scheduledStart) : formatDuration(lesson.scheduledStart, lesson.scheduledEnd)}
        </span>
      </div>

      {counterpart && (
        <Avatar name={counterpart.fullName} src={counterpart.avatarUrl} size="md" className="hidden sm:block" />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`${basePath}/lessons/${lesson.id}`}
            className="truncate font-medium text-slate-900 hover:text-brand-700"
          >
            {counterpart?.fullName ?? lesson.title}
            {extraStudents > 0 && (
              <span className="ml-1 text-sm font-normal text-slate-500">+{extraStudents} more</span>
            )}
          </Link>
          {lesson.subject && (
            <Badge tone="brand">{lesson.subject.name}</Badge>
          )}
          {lesson.status !== LessonStatus.SCHEDULED && (
            <Badge tone={STATUS_TONE[lesson.status]} dot={isLive}>
              {STATUS_LABEL[lesson.status]}
            </Badge>
          )}
        </div>

        <p className="mt-0.5 truncate text-sm text-slate-600">
          {lesson.topic ? lesson.topic.name : lesson.title}
        </p>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {lesson.canJoin ? (
          <Link to={`${basePath}/classroom/${lesson.id}`}>
            <Button size="sm" leftIcon={<Video className="h-4 w-4" aria-hidden />}>
              {perspective === 'tutor' ? 'Enter Classroom' : 'Join Classroom'}
            </Button>
          </Link>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-slate-400">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            {lesson.status === LessonStatus.COMPLETED ? 'Finished' : 'Not open yet'}
          </span>
        )}
      </div>
    </div>
  );
}
